/**
 * Shared preview constants for mock data.
 * This file must be server-safe (no browser APIs, no React hooks).
 * Used by Template Builder preview and the ad-renderer preview route.
 */

import { badge33, badge43 } from './badge';

export { badge33, badge43 };

export const PREVIEW_MAX_SIZE = 600;

export function getPreviewScaledSize(width: number, height: number) {
  const scaleX = width > PREVIEW_MAX_SIZE ? PREVIEW_MAX_SIZE / width : 1;
  const scaleY = height > PREVIEW_MAX_SIZE ? PREVIEW_MAX_SIZE / height : 1;
  const scale = Math.min(scaleX, scaleY);
  return {
    width: width * scale,
    height: height * scale,
    scale,
  };
}

/**
 * Get preview date (33 hours from now).
 * Function instead of const so it's fresh on each call.
 */
export const getPreviewDate = () => new Date(Date.now() + 33 * 60 * 60 * 1000);

/**
 * Mock data values for preview rendering.
 * Keys match the data point names used in templates.
 *
 * NOTE: Date fields (DAY, DATE, TIME, etc.) are NOT included here
 * because they need special handling (Date objects vs formatted strings).
 * Each consumer handles dates appropriately for their context.
 */
export const MockPreviewValues = {
  // Competition & Event
  COMPETITION_NAME: 'La Liga',
  EVENT_NAME: 'Some team vs Another',
  SPORT_NAME: 'Football',
  SPORT: 'Football',
  SPORT_ID: '1',
  VENUE: 'WWK Arena',
  VENUE_ID: '321',
  CITY_NAME: 'Augsburg',

  // Team Names
  HOME_NAME: 'Home Team',
  AWAY_NAME: 'Away Team',
  HOME_NAME_MICRO: 'HOM',
  AWAY_NAME_MICRO: 'AWA',
  COMPETITOR_NAME: 'Competitor',
  COMPETITOR_MICRO_NAME: 'COM',
  OPPONENT_NAME: 'Opponent',
  OPPONENT_MICRO_NAME: 'OPP',

  // Text dates (pre-formatted)
  TEXT_DATE: '19:00 tonight',
  TEXT_DATE_12: '07:00 PM tonight',

  // Odds (as tuples [numerator, denominator])
  ODDS: [10, 1] as [number, number],
  WAS_ODDS: [5, 1] as [number, number],
  HOME_ODDS: [12, 2] as [number, number],
  AWAY_ODDS: [5, 3] as [number, number],
  DRAW_ODDS: [20, 1] as [number, number],
  COMPETITOR_ODDS: [10, 1] as [number, number],
  OPPONENT_ODDS: [5, 1] as [number, number],
  OVER_ODDS: [10, 11] as [number, number],
  UNDER_ODDS: [10, 11] as [number, number],
  PROMO_ODDS: [10, 1] as [number, number],
  PROMO_WAS_ODDS: [5, 1] as [number, number],

  // Market
  MARKET_NAME: '90 Minutes',
  INSIGHT_TEXT: 'Insightful insight',
  // Keep in sync with BRANDED_INSIGHT_PLACEHOLDER in @adflow/data-getters event.ts
  BRANDED_INSIGHT: 'Placeholder for Branded Insight text that can be created in Content Studio',
  PROMO_TEXT:
    'The next person to score will also be the next person to get sent off for headbutting the Ref.',

  // Spreads & Totals
  TOTAL: '47.5',
  HOME_SPREAD: '+2.5',
  AWAY_SPREAD: '-2.5',

  // Percentages
  HOME_PERCENT_BETS: '71%',
  HOME_PERCENT_MONEY: '69%',
  AWAY_PERCENT_BETS: '17%',
  AWAY_PERCENT_MONEY: '18%',
  DRAW_PERCENT_BETS: '12%',
  DRAW_PERCENT_MONEY: '13%',

  // Stake
  STAKE: '£10',

  // IDs
  COMPETITOR_ID: '23',
  FOCUS_COMPETITOR_ID: '',
  OPPONENT_COMPETITOR_ID: '',
  HOME_COMPETITOR_ID: '',
  AWAY_COMPETITOR_ID: '',
  FOCUS_COMPETITOR_COUNTRY_CODE: '',
  OPPONENT_COMPETITOR_COUNTRY_CODE: '',
  HOME_COMPETITOR_COUNTRY_CODE: '',
  AWAY_COMPETITOR_COUNTRY_CODE: '',
  COMPETITION_ID: '17',

  // Colors
  HOME_COLOR_PRIMARY: '#224076',
  AWAY_COLOR_PRIMARY: '#D62D2E',
  HOME_COLOR_SECONDARY: '#FFFFFF',
  AWAY_COLOR_SECONDARY: '#FFFFFF',

  // Images
  LANDSCAPE_IMAGE: 'https://example.com/landscape.jpg',
  SQUARE_IMAGE: 'https://example.com/square.jpg',
  PORTRAIT_IMAGE: 'https://example.com/portrait.jpg',

  // Options (for multi-option markets like SGP, Competition Winner)
  OPTION_1_ODDS: [1, 1] as [number, number],
  OPTION_2_ODDS: [2, 1] as [number, number],
  OPTION_3_ODDS: [3, 1] as [number, number],
  OPTION_4_ODDS: [4, 1] as [number, number],
  OPTION_5_ODDS: [5, 1] as [number, number],
  OPTION_6_ODDS: [6, 1] as [number, number],
  OPTION_7_ODDS: [7, 1] as [number, number],
  OPTION_8_ODDS: [8, 1] as [number, number],
  OPTION_9_ODDS: [9, 1] as [number, number],
  OPTION_10_ODDS: [10, 1] as [number, number],
  OPTION_1_NAME: 'Option 1',
  OPTION_2_NAME: 'Option 2',
  OPTION_3_NAME: 'Option 3',
  OPTION_4_NAME: 'Option 4',
  OPTION_5_NAME: 'Option 5',
  OPTION_6_NAME: 'Option 6',
  OPTION_7_NAME: 'Option 7',
  OPTION_8_NAME: 'Option 8',
  OPTION_9_NAME: 'Option 9',
  OPTION_10_NAME: 'Option 10',
  OPTION_1_COMPETITOR_ID: '1',
  OPTION_2_COMPETITOR_ID: '2',
  OPTION_3_COMPETITOR_ID: '3',
  OPTION_4_COMPETITOR_ID: '4',
  OPTION_5_COMPETITOR_ID: '5',
  OPTION_6_COMPETITOR_ID: '6',
  OPTION_7_COMPETITOR_ID: '7',
  OPTION_8_COMPETITOR_ID: '8',
  OPTION_9_COMPETITOR_ID: '9',
  OPTION_10_COMPETITOR_ID: '10',
  OPTION_1_OUTCOME_NAME: 'Outcome 1',
  OPTION_2_OUTCOME_NAME: 'Outcome 2',
  OPTION_3_OUTCOME_NAME: 'Outcome 3',
  OPTION_4_OUTCOME_NAME: 'Outcome 4',
  OPTION_5_OUTCOME_NAME: 'Outcome 5',
  OPTION_1_MARKET_NAME: 'Market 1',
  OPTION_2_MARKET_NAME: 'Market 2',
  OPTION_3_MARKET_NAME: 'Market 3',
  OPTION_4_MARKET_NAME: 'Market 4',
  OPTION_5_MARKET_NAME: 'Market 5',

  // Odds Comparison (operator odds must be most favorable; comparisons sorted high→low)
  OPTION_NAME: 'Option Name',
  OPTION_NAME_ABBR: 'OPT',
  OPERATOR_ODDS: [19, 25] as [number, number],
  COMPARISON_1_NAME: 'Comparison Operator 1',
  COMPARISON_1_ODDS: [3, 4] as [number, number],
  COMPARISON_2_NAME: 'Comparison Operator 2',
  COMPARISON_2_ODDS: [2, 3] as [number, number],
  COMPARISON_3_NAME: 'Comparison Operator 3',
  COMPARISON_3_ODDS: [1, 2] as [number, number],

  // NASCAR specific
  OPTION_1_GRID_POSITION: '1',
  OPTION_1_DRIVER_NUMBER: '#1',
  OPTION_2_GRID_POSITION: '2',
  OPTION_2_DRIVER_NUMBER: '#2',
  OPTION_3_GRID_POSITION: '3',
  OPTION_3_DRIVER_NUMBER: '#3',
  OPTION_4_GRID_POSITION: '4',
  OPTION_4_DRIVER_NUMBER: '#4',
  OPTION_5_GRID_POSITION: '5',
  OPTION_5_DRIVER_NUMBER: '#5',
  RACE_NAME: 'Race Name',
  SOMETHING: 'Something',
  SOMETHING_ELSE: 'Something else',

  // NASCAR moment stages
  MOMENT_DRIVER_NAME: 'Driver Name',
  MOMENT_DRIVER_NUMBER: '#1',
  MOMENT_DRIVER_CUSTOM: '1',
  MOMENT_CAR_TEAM_NAME: 'Car Team Name',
  MOMENT_FASTEST_LAP: '42',
  MOMENT_FASTEST_LAP_TIME: '28.451',
  MOMENT_STAGE_NUMBER: '2',
  MOMENT_STAGE_LAPS_TOTAL: '120',
  MOMENT_STAGE_LAPS_COMPLETED: '85',
  MOMENT_POSITION: '1',

  // NBA Common
  TEAM_HOME_NAME: 'Team Home Name',
  TEAM_AWAY_NAME: 'Team Away Name',
  TEAM_HOME_NAME_SHORT: 'THN',
  TEAM_AWAY_NAME_SHORT: 'TAN',
  TEAM_HOME_NAME_FULL: 'Team Home Name Full',
  TEAM_AWAY_NAME_FULL: 'Team Away Name Full',

  // Soccer Common
  TEAM_HOME_COMPETITOR_ID: '101',
  TEAM_AWAY_COMPETITOR_ID: '102',
  TEAM_HOME_ABBREVIATION: 'HOM',
  TEAM_AWAY_ABBREVIATION: 'AWA',
  EVENT_ROUND_NAME: 'Matchday 12',
  MATCH_STATUS: 'HT',

  // NFL Common
  TEAM_HOME_MARKET: 'Kansas City',
  TEAM_AWAY_MARKET: 'San Francisco',

  // Soccer Lottery jackpots (display-ready; the getter formats raw amounts to this in production)
  LOTTERY_POWERBALL_JACKPOT: '940 million',
  LOTTERY_GERMAN6AUS49_JACKPOT: '4.4 billion',

  // Soccer Live Goal
  MOMENT_GOAL_SCORER_NAME: 'James Rodríguez',
  MOMENT_GOAL_MATCH_TIME: '72',
  MOMENT_GOAL_TEAM_COMPETITOR_ID: '101',

  // NFL Live
  LIVE_MATCH_TIME: '4:32',
  LIVE_MATCH_QUARTER: 'Q3',

  // NFL Live Touchdown
  MOMENT_TOUCHDOWN_SCORER_NAME: 'Patrick Mahomes',
  MOMENT_TOUCHDOWN_TEAM_NAME: 'Kansas City Chiefs',
  MOMENT_TOUCHDOWN_TEAM_MARKET: 'Kansas City',
  MOMENT_TOUCHDOWN_TEAM_SR_ID: '101',
  MOMENT_TOUCHDOWN_SCORER_SR_ID: '201',

  // Soccer Post
  POST_EVENT_OUTCOME: 'HOME_WIN',
  POST_WINNER_TEAM_NAME: 'Winning Team Name',

  // Soccer / NFL Post Winner
  POST_WINNER_TEAM_COMPETITOR_ID: '101',
  POST_WINNER_TEAM_MARKET: 'Kansas City',

  // NFL Post / Post Winner - Next Match
  POST_NEXT_MATCH_HOME_NAME: 'Next Home Team',
  POST_NEXT_MATCH_AWAY_NAME: 'Next Away Team',
  POST_NEXT_MATCH_HOME_MARKET: 'Buffalo',
  POST_NEXT_MATCH_AWAY_MARKET: 'Miami',
  POST_NEXT_MATCH_HOME_ABBREVIATION: 'BUF',
  POST_NEXT_MATCH_AWAY_ABBREVIATION: 'MIA',

  // NBA Pre-event
  PRE_TEAM_HOME_WINS: '21',
  PRE_TEAM_HOME_LOSSES: '18',
  PRE_TEAM_AWAY_WINS: '20',
  PRE_TEAM_AWAY_LOSSES: '19',

  // Live scores (defaults are soccer-scale; NBA overrides via MOCK_SCORE_OVERRIDES_BY_SOURCE_TYPE)
  LIVE_FULL_SCORE: '2 - 1',
  LIVE_TEAM_HOME_SCORE: '2',
  LIVE_TEAM_AWAY_SCORE: '1',
  LIVE_TOP_POINTS_PLAYER_NAME: 'Player Name',
  LIVE_TOP_POINTS_PLAYER_TEAM: 'Team Name',
  LIVE_TOP_POINTS_PLAYER_COUNT: '45',

  // NBA Post-event
  POST_FULL_SCORE: '142 - 133',
  POST_TEAM_HOME_SCORE: '142',
  POST_TEAM_AWAY_SCORE: '133',
  POST_TOP_POINTS_PLAYER_NAME: 'Player Name',
  POST_TOP_POINTS_PLAYER_TEAM: 'Team name',
  POST_TOP_POINTS_PLAYER_COUNT: '66',

  //Player Props
  HOME_POINTS_NAME: 'Home Player',
  HOME_POINTS_TOTAL: '18.5',
  HOME_POINTS_NAME_2: 'Home Player 2',
  HOME_POINTS_TOTAL_2: '17.5',
  HOME_POINTS_NAME_3: 'Home Player 3',
  HOME_POINTS_TOTAL_3: '16.5',
  HOME_POINTS_NAME_4: 'Home Player 4',
  HOME_POINTS_TOTAL_4: '15.5',
  HOME_POINTS_NAME_5: 'Home Player 5',
  HOME_POINTS_TOTAL_5: '14.5',
  AWAY_POINTS_NAME: 'Away Player',
  AWAY_POINTS_TOTAL: '22.5',
  AWAY_POINTS_NAME_2: 'Away Player 2',
  AWAY_POINTS_TOTAL_2: '21.5',
  AWAY_POINTS_NAME_3: 'Away Player 3',
  AWAY_POINTS_TOTAL_3: '20.5',
  AWAY_POINTS_NAME_4: 'Away Player 4',
  AWAY_POINTS_TOTAL_4: '19.5',
  AWAY_POINTS_NAME_5: 'Away Player 5',
  AWAY_POINTS_TOTAL_5: '18.5',
  TOP_POINTS_NAME: 'Top Scorer',
  TOP_POINTS_TOTAL: '31.5',
  TOP_REBOUNDS_NAME: 'Top Rebounder',
  TOP_REBOUNDS_TOTAL: '8.5',
  TOP_ASSISTS_NAME: 'Top Assister',
  TOP_ASSISTS_TOTAL: '6.5',
  TOP_POINTS_COMPETITOR_ID: undefined,
  TOP_REBOUNDS_COMPETITOR_ID: undefined,
  TOP_ASSISTS_COMPETITOR_ID: undefined,
  HOME_PITCHER_STRIKEOUTS_NAME: 'Home Pitcher Strikeouts',
  HOME_PITCHER_STRIKEOUTS_TOTAL: '10.5',
  AWAY_PITCHER_STRIKEOUTS_NAME: 'Away Pitcher Strikeouts',
  AWAY_PITCHER_STRIKEOUTS_TOTAL: '11.5',
  HOME_HITS_NAME: 'Home Hits',
  HOME_HITS_TOTAL: '13.5',
  HOME_HITS_NAME_2: 'Home Hits 2',
  HOME_HITS_TOTAL_2: '14.5',
  HOME_HITS_NAME_3: 'Home Hits 3',
  HOME_HITS_TOTAL_3: '15.5',
  HOME_HITS_NAME_4: 'Home Hits 4',
  HOME_HITS_TOTAL_4: '16.5',
  HOME_HITS_NAME_5: 'Home Hits 5',
  HOME_HITS_TOTAL_5: '17.5',
  AWAY_HITS_NAME: 'Away Hits',
  AWAY_HITS_TOTAL: '21.5',
  AWAY_HITS_NAME_2: 'Away Hits 2',
  AWAY_HITS_TOTAL_2: '22.5',
  AWAY_HITS_NAME_3: 'Away Hits 3',
  AWAY_HITS_TOTAL_3: '23.5',
  AWAY_HITS_NAME_4: 'Away Hits 4',
  AWAY_HITS_TOTAL_4: '24.5',
  AWAY_HITS_NAME_5: 'Away Hits 5',
  AWAY_HITS_TOTAL_5: '25.5',
  HOME_PASSING_YARDS_NAME: 'Home Passing Yards',
  HOME_PASSING_YARDS_TOTAL: '275.5',
  AWAY_PASSING_YARDS_TOTAL: '250.5',
  AWAY_PASSING_YARDS_NAME: 'Away Passing Yards',
  HOME_RUSHING_YARDS_NAME: 'Home Rushing Yards',
  HOME_RUSHING_YARDS_TOTAL: '85.5',
  AWAY_RUSHING_YARDS_NAME: 'Away Rushing Yards',
  AWAY_RUSHING_YARDS_TOTAL: '75.5',
  HOME_RECEIVING_YARDS_NAME: 'Home Receiving Yards',
  HOME_RECEIVING_YARDS_TOTAL: '65.5',
  AWAY_RECEIVING_YARDS_NAME: 'Away Receiving Yards',
  AWAY_RECEIVING_YARDS_TOTAL: '55.5',

  // Outright
  OUTRIGHT_NAME: 'Outright',

  // Video
  MAIN_VIDEO: undefined,
} as const;

type ScoreOverride = {
  LIVE_FULL_SCORE: string;
  LIVE_TEAM_HOME_SCORE: string;
  LIVE_TEAM_AWAY_SCORE: string;
  POST_FULL_SCORE: string;
  POST_TEAM_HOME_SCORE: string;
  POST_TEAM_AWAY_SCORE: string;
};

/**
 * Sport-scoped overrides for score mock values.
 * Defaults stay as-is in MockPreviewValues and only divergent source types need entries.
 */
export const MOCK_SCORE_OVERRIDES_BY_SOURCE_TYPE: Record<string, Partial<ScoreOverride>> = {
  NBA_LIVE: {
    LIVE_FULL_SCORE: '123 - 98',
    LIVE_TEAM_HOME_SCORE: '123',
    LIVE_TEAM_AWAY_SCORE: '98',
  },
  NBA_POST_EVENT: {
    POST_FULL_SCORE: '142 - 133',
    POST_TEAM_HOME_SCORE: '142',
    POST_TEAM_AWAY_SCORE: '133',
  },
  SOCCER_POST: {
    POST_FULL_SCORE: '2 - 1',
    POST_TEAM_HOME_SCORE: '2',
    POST_TEAM_AWAY_SCORE: '1',
  },
  SOCCER_POST_WINNER: {
    POST_FULL_SCORE: '2 - 1',
    POST_TEAM_HOME_SCORE: '2',
    POST_TEAM_AWAY_SCORE: '1',
  },
  NFL_LIVE: {
    LIVE_FULL_SCORE: '24 - 17',
    LIVE_TEAM_HOME_SCORE: '24',
    LIVE_TEAM_AWAY_SCORE: '17',
  },
  NFL_LIVE_TOUCHDOWN: {
    LIVE_FULL_SCORE: '24 - 17',
    LIVE_TEAM_HOME_SCORE: '24',
    LIVE_TEAM_AWAY_SCORE: '17',
  },
  NFL_POST: {
    POST_FULL_SCORE: '28 - 24',
    POST_TEAM_HOME_SCORE: '28',
    POST_TEAM_AWAY_SCORE: '24',
  },
  NFL_POST_WINNER: {
    POST_FULL_SCORE: '28 - 24',
    POST_TEAM_HOME_SCORE: '28',
    POST_TEAM_AWAY_SCORE: '24',
  },
};

const SCORE_KEYS = new Set<string>([
  'LIVE_FULL_SCORE',
  'LIVE_TEAM_HOME_SCORE',
  'LIVE_TEAM_AWAY_SCORE',
  'POST_FULL_SCORE',
  'POST_TEAM_HOME_SCORE',
  'POST_TEAM_AWAY_SCORE',
]);

export function isScoreKey(key: string): key is keyof ScoreOverride {
  return SCORE_KEYS.has(key);
}

/**
 * Resolve a mock value for a score key, using sport-scoped overrides when available.
 * Falls back to the default in MockPreviewValues.
 */
export function getMockScoreValue(key: string, sourceType?: string): string | undefined {
  if (!isScoreKey(key)) return undefined;
  if (sourceType) {
    const override = MOCK_SCORE_OVERRIDES_BY_SOURCE_TYPE[sourceType];
    const overrideValue = override?.[key];
    if (overrideValue !== undefined) return overrideValue;
  }
  return MockPreviewValues[key];
}

/**
 * Team asset display data for HOME and AWAY teams.
 */
export const MockTeamAssets = {
  HOME_ASSET: {
    JERSEY: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#D62D2E',
          background: '#224076',
        },
      },
    },
    BADGE: {
      type: 'badge' as const,
      data: {
        content: badge33,
      },
    },
    CUSTOM: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#D62D2E',
          background: '#224076',
        },
      },
    },
  },
  AWAY_ASSET: {
    JERSEY: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#224076',
          background: '#D62D2E',
        },
      },
    },
    BADGE: {
      type: 'badge' as const,
      data: {
        content: badge43,
      },
    },
    CUSTOM: {
      type: 'kit' as const,
      data: {
        kind: 'shortSleeveJersey' as const,
        colours: {
          sleeve: '#224076',
          background: '#D62D2E',
        },
      },
    },
  },
};

/**
 * Vector color palettes for preview.
 */
export const MockVectorPalettes = [
  {
    HOME_COLOR: { PRIMARY: 'red', SECONDARY: 'green' },
    AWAY_COLOR: { PRIMARY: 'blue', SECONDARY: 'cyan' },
    COMPETITOR_COLOR: { PRIMARY: 'yellow', SECONDARY: 'pink' },
    OPPONENT_COLOR: { PRIMARY: 'white', SECONDARY: 'purple' },
  },
  {
    HOME_COLOR: { PRIMARY: 'magenta', SECONDARY: 'yellow' },
    AWAY_COLOR: { PRIMARY: 'black', SECONDARY: 'purple' },
    COMPETITOR_COLOR: { PRIMARY: 'yellow', SECONDARY: 'pink' },
    OPPONENT_COLOR: { PRIMARY: 'white', SECONDARY: 'purple' },
  },
];

/**
 * Date-related data point keys.
 */
export const DATE_DATA_POINTS = [
  'DAY',
  'DATE',
  'MONTH',
  'TIME',
  'COUNTDOWN',
  'PRE_COUNTDOWN',
] as const;

export type DateDataPoint = (typeof DATE_DATA_POINTS)[number];

export function isDateDataPoint(key: string): key is DateDataPoint {
  return DATE_DATA_POINTS.includes(key as DateDataPoint);
}
